/**
 * Dimension types and unit lists for the edit-object Hi-Fi MeasurementsSection.
 *
 * The upload-object wizard's MeasurementField renders a subset of these types
 * (excludes Height/Width — those are pre-populated rows in MeasureTab) via
 * translation keys. Unit categorization (linear / weight / volume) and the
 * unit lists themselves are intended to match across both surfaces — update
 * MeasurementField.tsx alongside this module if a category mapping changes.
 */

export const DIMENSION_TYPES = [
  'Height',
  'Width',
  'Depth',
  'Diameter',
  'Length',
  'Thickness',
  'Radius',
  'Circumference',
  'Weight',
  'Mass',
  'Volume',
] as const;

export type DimensionType = (typeof DIMENSION_TYPES)[number];

export const LINEAR_UNITS = ['inches', 'cm', 'mm', 'feet', 'meters'] as const;
export const WEIGHT_UNITS = ['lbs', 'oz', 'kg', 'g'] as const;
export const VOLUME_UNITS = ['L', 'mL', 'gal', 'fl_oz', 'cc'] as const;

export type DimensionCategory = 'linear' | 'weight' | 'volume';

const WEIGHT_TYPES: ReadonlySet<string> = new Set(['Weight', 'Mass']);
const VOLUME_TYPES: ReadonlySet<string> = new Set(['Volume']);

export const getDimensionCategory = (type: string): DimensionCategory => {
  if (WEIGHT_TYPES.has(type)) return 'weight';
  if (VOLUME_TYPES.has(type)) return 'volume';
  return 'linear';
};

export const getUnitsForDimension = (
  type: string,
): ReadonlyArray<string> => {
  switch (getDimensionCategory(type)) {
    case 'weight':
      return WEIGHT_UNITS;
    case 'volume':
      return VOLUME_UNITS;
    default:
      return LINEAR_UNITS;
  }
};

export const getDefaultUnitForDimension = (type: string): string => {
  switch (getDimensionCategory(type)) {
    case 'weight':
      return 'lbs';
    case 'volume':
      return 'L';
    default:
      return 'inches';
  }
};
